import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ListingOrder, type LanguageCode } from '@linktivity/ars-neo';
import { Images, ImagePlaceholder } from '@link/share/components/Images';
import { SearchBar } from '@link/share/components/SearchBar';
import { useIsMobile } from '@link/share/hooks/useIsMobile';
import { buildLocalizedPath } from '@link/share/utils';
import { resolveAppLocale } from '../../i18n';
import { metaT, triplaboSeo } from '../../seo';
import type { Route } from './+types/Home';
import ExpandableProductList from './ExpandableProductList';
import { loadHome } from './home.server';
import SearchType from './SearchType';
import styles from './home.module.css';

export async function loader({ params, request }: Route.LoaderArgs) {
  const locale = resolveAppLocale(params.lang);
  // The API takes an uppercase enum ('JA'); a lowercase locale is a 400.
  // `resolveAppLocale` only returns locales the SDK also has codes for, so the
  // cast narrows a widened `string` rather than asserting an unchecked shape.
  const data = await loadHome(locale.toUpperCase() as LanguageCode);
  // `meta` receives only loader data, and canonical/hreflang/og:url all need an
  // absolute URL. The server has no `window.location`, so the request URL is
  // passed through here — it is also the only source that stays correct across
  // test, production and preview hostnames without per-environment config.
  return { ...data, locale, url: request.url };
}

/**
 * Page metadata, rendered into the server HTML.
 *
 * Replaces the SPA's `useSEO()` effect, which could only set tags after the
 * bundle ran — crawlers that do not execute JavaScript never saw them.
 *
 * The site schema carries no SEO fields (verified against the API: `siteLayout`
 * has authConfig/headerConfig/footerConfig/landingPageLayout/productPageLayout
 * and no seoConfig), so the copy comes from translations, matching what
 * `triplaboSeo()` fed the SPA.
 *
 * The home page exists in every language and has no content-bearing query
 * params, so the hreflang and canonical defaults apply unchanged.
 */
export function meta({ loaderData, matches }: Route.MetaArgs) {
  const t = metaT(matches[0].loaderData);
  // `loaderData` is undefined when the loader threw: the ErrorBoundary renders
  // in place of the page, but `meta` still runs. Emit a bare title and nothing
  // else — a canonical or hreflang set built from a guessed origin would invite
  // crawlers to index an error page as if it were the real one, and the request
  // URL only reaches here through loader data that does not exist on this path.
  if (!loaderData) {
    return [
      { title: t('triplabo.home.title') },
      { name: 'robots', content: 'noindex, nofollow' }
    ];
  }
  return triplaboSeo(t, loaderData.locale, loaderData.url);
}

export default function Home({ loaderData }: Route.ComponentProps) {
  const { site, recentlyList, sections, recommendList, currency, locale } =
    loaderData;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const showOtherCurrencies =
    site.siteLayout?.headerConfig?.showOtherCurrencies ?? false;

  // Drop slides with no image before splitting into the two parallel arrays
  // Images takes — an entry with no mediaUrl would render a broken slide, and
  // filtering only one of the arrays would misalign images against their links.
  const banner = (
    site.siteLayout?.landingPageLayout?.bannerConfig?.medias ?? []
  ).filter(media => !!media.mediaUrl);
  const bannerImages = banner.map(media => media.mediaUrl ?? '');
  const bannerUrls = banner.map(media => media.link?.url ?? '');

  const handleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const trimmed = value.trim();
      if (trimmed) {
        navigate(
          buildLocalizedPath(
            `activity?search=${encodeURIComponent(trimmed)}&sort=${ListingOrder.ListingOrderPriority}`,
            locale
          )
        );
      }
    },
    [navigate, locale]
  );

  const productRow = (products: typeof recentlyList) => (
    <ExpandableProductList
      products={products}
      locale={locale}
      currency={currency}
      showOtherCurrencies={showOtherCurrencies}
    />
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.banner}>
        <div className={styles.bannerImageWrapper}>
          {/* No loading skeleton: the loader has already resolved, so the
              banner is present in the very first paint. */}
          {bannerImages.length > 0 ? (
            <Images
              showArrowOnMobile={false}
              fullWidth
              images={bannerImages}
              urls={bannerUrls}
              className={styles.bannerImage}
            />
          ) : (
            <ImagePlaceholder className={styles.bannerImagePlaceholder} />
          )}
          <SearchBar
            variant={isMobile ? 'icon-button' : 'text-button'}
            searchLabel={t('share.common.search')}
            placeholder={t('triplabo.search.placeholder')}
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            className={styles.searchBarWrapper}
            containerClassName={styles.searchBarContainer}
          />
        </div>
      </div>

      <div className={styles.content}>
        <SearchType locale={locale} />

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionHeadingGradient}>
              {t('triplabo.home.recently')}
            </span>
          </h2>
          {productRow(recentlyList)}
        </div>

        {sections.map((section, index) => (
          <div
            key={`${section.title ?? 'site-section'}-${index}`}
            className={styles.section}
          >
            <h2 className={styles.sectionHeading}>
              <span className={styles.sectionHeadingGradient}>
                {section.title || t('triplabo.home.events')}
              </span>
            </h2>
            {productRow(section.products)}
          </div>
        ))}

        <div className={styles.section}>
          <h2 className={styles.sectionHeading}>
            <span className={styles.sectionHeadingGradient}>
              {t('triplabo.home.recommend')}
            </span>
          </h2>
          {productRow(recommendList)}
        </div>
      </div>
    </div>
  );
}
